import { zodResolver } from '@hookform/resolvers/zod';
import { Add, KeyboardArrowDown } from '@mui/icons-material';
import {
    Autocomplete,
    Button,
    Grid,
    Paper,
    TextField,
    useTheme,
} from '@mui/material';
import React from 'react';
import {
    Controller,
    FieldValues,
    useForm,
    useFormContext,
} from 'react-hook-form';
import * as z from 'zod';
import { useGetAllGuestAttendeesQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import { User } from '../../../types/user';

export default function StaffAttending({
    handleSubmitMain,
    disableSendButton,
    adults,
    isSmallChat,
    setStaffLength,
}: any) {
    const { setValue } = useFormContext();

    const loggedInUser = UserRole();

    const schema = z.object({
        staffAttending: z
            .array(
                z.object({
                    id: z.number(),
                    email: z.string(),
                    firstName: z.string(),
                    lastName: z.string(),
                }),
            )
            .default([]),
    });

    type Schema = z.infer<typeof schema>;

    const methods = useForm<Schema>({
        resolver: zodResolver(schema),
        mode: 'onChange',
        shouldUnregister: false,
        shouldFocusError: true,
    });

    const { control, handleSubmit, watch } = methods;

    const theme = useTheme();

    const { data: guestAttendees } = useGetAllGuestAttendeesQuery(
        loggedInUser?.company,
    );

    const onSubmit = async (data: FieldValues) => {
        setValue('staffAttending', data.staffAttending);
        setValue('renderMessage', false);
        setValue('disableSendButton', true);

        handleSubmitMain();
    };

    const staffAttending = watch('staffAttending');

    const disable = () => {
        if (disableSendButton) {
            return true;
        }

        if (staffAttending?.length > adults) {
            return true;
        }

        return false;
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    alignItems: 'flex-end',
                }}
            >
                <Grid sx={{ width: isSmallChat ? '350px' : '400px' }}>
                    <Controller
                        name='staffAttending'
                        control={control}
                        render={({
                            field: { onChange, value },
                            fieldState: { error },
                        }) => {
                            const selected = Array.isArray(value) ? value : [];

                            setStaffLength(selected.length);

                            return (
                                <Autocomplete
                                    PaperComponent={({ children }) => {
                                        return (
                                            <Paper>
                                                <Button
                                                    fullWidth
                                                    size='large'
                                                    sx={{
                                                        justifyContent:
                                                            'flex-start',
                                                        pl: 2,
                                                        mt: 1,
                                                    }}
                                                    startIcon={<Add />}
                                                    onMouseDown={(e) => {
                                                        e.stopPropagation();
                                                        window.open(
                                                            '/newUser',
                                                            '_blank',
                                                        );
                                                    }}
                                                >
                                                    Create New User
                                                </Button>
                                                {children}
                                            </Paper>
                                        );
                                    }}
                                    size='small'
                                    onChange={(_, data) =>
                                        onChange(data.map((d) => d.value))
                                    }
                                    value={selected.map((user) => ({
                                        value: user,
                                        label: `${user.firstName} ${user.lastName} (${user.email})`,
                                    }))}
                                    disabled={disableSendButton}
                                    getOptionDisabled={(_) =>
                                        selected.length >= adults
                                    }
                                    multiple
                                    disableCloseOnSelect
                                    defaultValue={[]}
                                    filterSelectedOptions
                                    isOptionEqualToValue={(option, value) =>
                                        option.value.id === value.value.id
                                    }
                                    options={
                                        guestAttendees?.map((user: User) => ({
                                            value: user,
                                            label: `${user.firstName} ${user.lastName} (${user.email})`,
                                        })) || []
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} />
                                    )}
                                    popupIcon={<KeyboardArrowDown />}
                                />
                            );
                        }}
                    />
                </Grid>

                <Grid sx={{ width: 'auto' }}>
                    <CustomButton
                        size='small'
                        color='primary'
                        variant='contained'
                        onClick={handleSubmit(onSubmit)}
                        disableElevation
                        disableRipple
                        disabled={disable()}
                        sx={{
                            height: '100%',
                            display: 'flex',
                            '&:hover': {
                                cursor: 'pointer',
                                color: theme.palette.textAccent.contrastText,
                                backgroundColor: theme.palette.textAccent.main,
                            },
                        }}
                    >
                        {!staffAttending || staffAttending?.length === 0
                            ? 'Submit without selecting staff'
                            : 'Submit'}
                    </CustomButton>
                </Grid>
            </Grid>
        </form>
    );
}
